/**
 * Money rendering — the ONE place an amount becomes a string.
 *
 * Chapter 10 (F7). Three testers reported "there need to be dots and comma's at
 * the final prices". The UI rendered `toFixed()` + `toLocaleString('en-US')` at
 * ~18 sites, so a Dutch customer read "€1,234.56" — and, worse, the same order
 * disagreed with itself: the screen rounded totals with `toFixed(0)` ("€935")
 * while the email formatted two decimals ("€ 934,50").
 *
 * This lives in `content-schema` rather than in either app because both apps
 * and the email template must agree by construction, and this package is
 * already the shared home for presentation helpers of exactly this kind — see
 * `describeBand` in `pricingBands.ts`.
 *
 * The rule, in one line: **money renders as nl-NL with exactly two decimals,
 * and rendering never re-rounds a computed value into a different number.**
 * Display rounding happens here and nowhere else; the pricing engine's numbers
 * are left alone.
 */

/** Exactly two decimals, nl-NL grouping. Not exported — the two public
 *  functions below are the only entry points. */
const FORMATTER = new Intl.NumberFormat('nl-NL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * `1234.56` → `"1.234,56"`. No currency symbol.
 *
 * For markup that styles the symbol separately from the digits — several
 * surfaces render the currency in its own element at a different size.
 */
export function formatAmount(amount: number): string {
  // A rounding residue of -0.0001 would render "-0,00", which reads as a defect
  // on a customer's quote. Genuine negatives keep their sign; only the
  // rounds-to-nothing case is normalised.
  const normalised = Object.is(amount, -0) || (amount < 0 && amount > -0.005) ? 0 : amount;
  return FORMATTER.format(normalised);
}

/**
 * `1234.56` → `"€ 1.234,56"`.
 *
 * `currencySymbol` defaults to the euro but is accepted because several call
 * sites carry it in data (`cart.pricing.currency`). One space between symbol
 * and amount — the contract `types.ts` documents for `totalPrice` and what the
 * shipped email already produced.
 *
 * A non-finite amount renders visibly broken ("€ NaN") on purpose. A NaN total
 * means the pricing engine failed, and showing "€ 0,00" instead would present a
 * customer with a free order while hiding the fault.
 */
export function formatMoney(amount: number, currencySymbol = '€'): string {
  return `${currencySymbol} ${formatAmount(amount)}`;
}
